import { Navigate, Route, Routes, useLocation, type Location } from "react-router";
import { Suspense, type ReactNode } from "react";
import { Spinner } from "@carbon6/c6-frontend-design";
import { RendererLab } from "@/modules/inspect/public";
import { authCallbackPath, defaultSignedInPath } from "@/modules/auth/public";
import { CandidateListPage, DatasetDetailPage, DatasetListPage } from "@/modules/datasets/public";
import { AuthCallbackPage } from "../auth/AuthCallbackPage";
import { LandingPage } from "../auth/LandingPage";
import { GnbMenu, gnbMenuEntries } from "../shell/Gnb";
import { useAppState } from "../state/AppStateProvider";
import { PlaceholderPage } from "./PlaceholderPage";
import { RouteScrollManager } from "./RouteScrollManager";

// 실제 화면을 가진 메뉴는 아래 명시 라우트가 잡고, 나머지는 그 메뉴가 맡을 일을 적은
// placeholder 하나로 선다. 경로·라벨은 Gnb 의 메뉴 표가 소유하고 여기서는 설명만 붙인다.
type PlaceholderMenu = Exclude<GnbMenu, typeof GnbMenu.Datasets | typeof GnbMenu.ScannedResults>;
const placeholderDescriptions: Record<PlaceholderMenu, string> = {
  [GnbMenu.Conversions]:
    "조직의 전량 변환 · Registry 등록 실행을 살피고 실패 처리와 등록 재시도를 다루는 화면입니다.",
  [GnbMenu.Templates]:
    "이미지 릴리스와 함께 배포된 Template 카탈로그와 InputParams schema 를 읽는 화면입니다.",
  [GnbMenu.OperationsWorkers]:
    "수집 Worker 의 상태를 관측하는 화면입니다. 운영 변경은 CLI 가 맡습니다.",
  [GnbMenu.OperationsAirflow]:
    "Airflow dispatcher · reconciler 와 outbox 동기화 현황을 관측하는 화면입니다.",
};

const placeholderMenus = Object.values(GnbMenu).filter(
  (menu): menu is PlaceholderMenu => menu !== GnbMenu.Datasets && menu !== GnbMenu.ScannedResults,
);

export function AppRouter() {
  const { state } = useAppState();
  const isSignedIn = state.auth.status === "signedIn";

  return (
    <>
      <RouteScrollManager />
      <Routes>
        {import.meta.env.DEV && (
          <Route
            path="/dev/renderer"
            element={
              <Suspense fallback={<Spinner label="검증 화면을 준비하는 중" />}>
                <RendererLab />
              </Suspense>
            }
          />
        )}
        <Route path={authCallbackPath} element={<AuthCallbackPage />} />
        <Route
          path="/"
          element={isSignedIn ? <Navigate to={defaultSignedInPath} replace /> : <LandingPage />}
        />
        <Route
          path={gnbMenuEntries[GnbMenu.ScannedResults].to}
          element={
            <RequireAuth>
              <CandidateListPage />
            </RequireAuth>
          }
        />
        <Route
          path={gnbMenuEntries[GnbMenu.Datasets].to}
          element={
            <RequireAuth>
              <DatasetListRoute />
            </RequireAuth>
          }
        />
        {/* Probe uuid 를 경로가 드는 이유는 modules/datasets 의 `routes.ts` 가 적는다. */}
        {[":datasetUuid", ":datasetUuid/probes/:probeUuid"].map((tail) => (
          <Route
            key={tail}
            path={`${gnbMenuEntries[GnbMenu.Datasets].to}/${tail}`}
            element={
              <RequireAuth>
                <DatasetDetailPage />
              </RequireAuth>
            }
          />
        ))}
        {placeholderMenus.map((menu) => (
          <Route
            key={menu}
            path={gnbMenuEntries[menu].to}
            element={
              <RequireAuth>
                <PlaceholderPage
                  pageTitle={gnbMenuEntries[menu].label}
                  currentMenu={menu}
                  description={placeholderDescriptions[menu]}
                />
              </RequireAuth>
            }
          />
        ))}
        {/* 모르는 경로는 랜딩으로 보낸다 — 인증 게이트가 아니라 목적지를 만들지 않는다. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/** 예전 탭 링크의 검색 조건을 보존해 별도 페이지로 보낸다. */
function DatasetListRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get("tab") === "discovered") {
    params.delete("tab");
    params.delete("include_hidden");
    return <Navigate to={`/scanned-results${params.size ? `?${params}` : ""}`} replace />;
  }
  return <DatasetListPage />;
}

// 미인증 진입은 랜딩으로 보내되 원래 목적지를 history state 로 넘겨 로그인 뒤 돌아오게 한다.
function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAppState();
  const location = useLocation();

  if (state.auth.status !== "signedIn") {
    return <Navigate to="/" replace state={{ from: locationToPath(location) }} />;
  }

  return children;
}

function locationToPath(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}
